// Texture keys shared between the art builders and the systems that use them.
// Builders create every key listed here (procedurally) unless a file with the
// same key was loaded from public/assets/manifest.json first.

import type { ParallaxLayer } from '../config/constants';
import type {
  BiomeId,
  EquipSlot,
  NodeType,
  OrbType,
  PassiveId,
  SignatureId,
  Tag,
  UltimateId,
} from '../core/types';

export const TEX = {
  // Generic FX (white, tint at runtime)
  pixel: 'fx_pixel', // 4×4 white square
  soft: 'fx_soft', // 64×64 radial soft glow
  ring: 'fx_ring', // 128×128 thin ring
  spark: 'fx_spark', // 24×6 elongated spark
  muzzle: 'fx_muzzle', // 64×40 muzzle flash star (points right)
  smoke: 'fx_smoke', // 64×64 soft puff
  ember: 'fx_ember', // 8×8 hot dot
  shard: 'fx_shard', // 12×8 debris chip
  explosion: 'fx_explosion', // spritesheet, 8 frames of 128×128
  shield: 'fx_shield', // 128×128 hex shield bubble
  rain: 'fx_rain', // 4×40 rain streak
  bolt: 'fx_bolt', // 32×8 lightning segment glow (tileable along x)
  trailElectric: 'fx_trail_electric',
  trailBurn: 'fx_trail_burn',
  trailPlasma: 'fx_trail_plasma',
  trailVoid: 'fx_trail_void',
  chevron: 'fx_chevron', // 32×32 warning chevron (points down)
  hex: 'fx_hex', // 32×32 hex cell

  // Projectiles (drawn pointing right)
  projPulse: 'proj_pulse',
  projPellet: 'proj_pellet',
  projRail: 'proj_rail',
  projRocket: 'proj_rocket',
  projGravity: 'proj_gravity',
  projEnemyBolt: 'proj_enemy_bolt',
  projEnemyOrb: 'proj_enemy_orb',
  projEnemyNeedle: 'proj_enemy_needle',
  projMortar: 'proj_mortar',

  // Pickups / interactables
  orbShell: { arsenal: 'orb_arsenal', armor: 'orb_armor', unstable: 'orb_unstable' } as Record<OrbType, string>,
  holoBase: 'holo_base', // item hologram pedestal
  scrap: 'pickup_scrap',
  health: 'pickup_health',
  door: 'prop_door', // spritesheet 48×144: frame 0 locked, 1 open
  exitGate: 'prop_exit', // 160×208 route-exit gate (frame 0 inactive, 1 active)
  crate: 'prop_crate', // 64×64 breakable
  terminal: 'prop_terminal', // 64×96 challenge terminal
  station: 'prop_station', // 192×192 service station kiosk
  repairBay: 'prop_repair', // 160×160 repair bay
  spawnPortal: 'fx_portal', // 128×128 portal ring
  reticle: 'ui_reticle', // 48×48 crosshair
} as const;

/** Tileset texture key per biome (spritesheet of 48×48 frames, 8 columns). */
export const tilesetKey = (biome: BiomeId): string => `tiles_${biome}`;

/**
 * Tileset frame layout.
 * Frames 0..15: solid autotile, frame index = neighbour mask
 *   (bit 1 = solid above, 2 = right, 4 = below, 8 = left). Exposed top edges carry the lit trim.
 */
export const TILE_FRAMES = {
  solidMaskBase: 0, // + mask (0..15)
  solidDetailA: 16, // interior variants for large fills (only used when mask === 15)
  solidDetailB: 17,
  platformLeft: 18,
  platformMid: 19,
  platformRight: 20,
  platformSingle: 21,
  hazard: 22,
  backWall: 23, // non-colliding background wall
  decorVent: 24,
  decorPipe: 25,
  decorGrate: 26,
  decorStripes: 27,
} as const;
export const TILESET_COLUMNS = 8;
export const TILESET_FRAME_COUNT = 28;

export const parallaxKey = (biome: BiomeId, layer: ParallaxLayer): string => `bg_${biome}_${layer}`;

export const iconKey = {
  slot: (s: EquipSlot | 'weapon' | 'mod' | 'mutation') => `icon_slot_${s}`,
  tag: (t: Tag) => `icon_tag_${t}`,
  signature: (id: SignatureId) => `icon_sig_${id}`,
  ultimate: (id: UltimateId) => `icon_ult_${id}`,
  passive: (id: PassiveId) => `icon_passive_${id}`,
  node: (t: NodeType) => `icon_node_${t}`,
  orb: (t: OrbType) => `icon_orb_${t}`,
  mod: 'icon_mod',
  mutation: 'icon_mutation',
  upgrade: 'icon_upgrade',
  scrap: 'icon_scrap',
  core: 'icon_core',
  heat: 'icon_heat',
  lock: 'icon_lock',
} as const;

/** Mech art keys (created by src/art/mech). */
export const mechKeys = {
  sheet: (id: string) => `mech_${id}_body`,
  glow: (id: string) => `mech_${id}_glow`,
  preview: (id: string) => `mech_${id}_preview`,
  portrait: (id: string) => `mech_${id}_portrait`,
  armUpper: (id: string) => `mech_${id}_arm_upper`,
  armFore: (id: string) => `mech_${id}_arm_fore`,
  anim: (id: string, anim: string) => `${id}_${anim}`,
  weapon: (id: string) => `weapon_${id}`,
  weaponGlow: (id: string) => `weapon_${id}_glow`,
  equip: (id: string, part: string) => `equip_${id}_${part}`,
} as const;
